<template>
  <div class="row m-3">
    <loading :active.sync="isLoading" :is-full-page="true"></loading>
    <div class="col-12 mb-2">
      <h1>
        {{ $t("ADMIN_LOGS_TITLE") }}
      </h1>
    </div>

    <div class="col-md-12 mb-3">
      <div class="card bg-light">
        <div class="card-body">
          <div class="row">
            <div class="col-md-12">
              <h5 class="card-title">
                {{ $t("ADMIN_LOGS_FILTER") }}
              </h5>
            </div>
          </div>
          <div class="row">

            <div class="col-xl-2 col-md-4 col-sm-6 col-xs-12">
              <label for="date_start_filter" class="text-primary font-weight-light">
                <i class="fas fa-calendar-alt"></i>
                {{ $t("ADMIN_LOGS_FILTER_START_DATE") }}
              </label>
              <datetime v-model="filters.start" id="date_start_filter" input-class="form-control"
                        type="datetime" :minute-step="5" format="y-LL-dd HH:mm:ss" class="theme-blue"
                        :max-datetime="filters.end" title="Start date"
              ></datetime>
            </div>
            <div class="col-xl-2 col-md-4 col-sm-6 col-xs-12">
              <label for="date_end_filter" class="text-primary font-weight-light">
                <i class="fas fa-calendar-alt"></i>
                {{ $t("ADMIN_LOGS_FILTER_END_DATE") }}
              </label>
              <datetime v-model="filters.end" id="date_end_filter" input-class="form-control"
                        type="datetime" :minute-step="5" format="y-LL-dd HH:mm:ss" class="theme-blue"
                        :min-datetime="filters.start" title="End ate"></datetime>
            </div>

            <div class="col-xl-2 col-md-4 col-sm-6 col-xs-12">
              <label for="level_filters" class="text-primary font-weight-light">
                <i class="fas fa-layer-group"></i>
                {{ $t("ADMIN_LOGS_FILTER_LOG_LEVELS") }}
              </label>
              <v-select id="level_filters" label="key" class="bg-white" :options="availableLogLevels" v-model="selectedLogLevels" :multiple="true" :placeholder="$t('ADMIN_LOGS_SELECT_LOG_LEVEL_PLACEHOLDER')"></v-select>
            </div>
            <div class="col-xl-2 col-md-4 col-sm-6 col-xs-12">
              <label for="search_filter" class="text-primary font-weight-light">
                <i class="fas fa-search"></i>
                {{ $t("ADMIN_LOGS_FILTER_SEARCH") }}
              </label>
              <input type="text" id="search_filter" v-model="filters.search" class="form-control" :placeholder="$t('ADMIN_LOGS_SEARCH_TYPE_MESSAGE_PLACEHOLDER')" v-on:keyup.enter="fetchLogs">
            </div>
            <div class="col-xl-2 col-md-4 col-sm-6 col-xs-12">
              <label for="user_filter" class="text-primary font-weight-light">
                <i class="fas fa-user-alt"></i>
                {{ $t("ADMIN_LOGS_FILTER_USER") }}
              </label>
              <input type="text" id="user_filter" v-model="filters.user" class="form-control" :placeholder="$t('ADMIN_LOGS_SEARCH_USER_PLACEHOLDER')" v-on:keyup.enter="fetchLogs">
            </div>
            <div class="col-xl-2 col-md-4 col-sm-6 col-xs-12">
              <label for="channel_filter" class="text-primary font-weight-light">
                <i class="fas fa-tv"></i>
                {{ $t("ADMIN_LOGS_FILTER_CHANNEL") }}
              </label>
              <v-select id="channel_filter" label="title" class="bg-white" :options="channels" v-model="filters.channel" :placeholder="$t('ADMIN_LOGS_SEARCH_CHANNEL_PLACEHOLDER')"></v-select>
            </div>

          </div>

          <div class="row mt-3">
            <div class="col-md-12">
              <button @click="fetchLogs" class="btn btn-primary">
                {{ $t("ADMIN_LOGS_FILTER_APPLY") }}
              </button>
              <button class="btn btn-outline-secondary ml-2" @click="resetFilters">
                {{ $t("ADMIN_LOGS_FILTER_RESET") }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-md-12">
      <div class="table-responsive">
        <table class="table table-striped table-condensed">
          <thead>
          <tr>
            <th>
              <i class="fas fa-xs fa-calendar-alt"></i> {{ $t("ADMIN_LOGS_TABLE_DATE_TIME") }}
            </th>
            <th>
              <i class="fas fa-xs fa-layer-group"></i> {{ $t("ADMIN_LOGS_TABLE_LOG_LEVEL") }}
            </th>
            <th>
              <i class="fas fa-xs fa-sitemap"></i> {{ $t("ADMIN_LOGS_TABLE_TYPE") }}
            </th>
            <th>
              <i class="fas fa-xs fa-user-alt"></i> {{ $t("ADMIN_LOGS_TABLE_USER") }}
            </th>
            <th>
              <i class="fas fa-xs fa-comment"></i> {{ $t("ADMIN_LOGS_TABLE_MESSAGE") }}
            </th>
            <th>
              <i class="fas fa-xs fa-tv"></i> {{ $t("ADMIN_LOGS_TABLE_CHANNEL") }}
            </th>
            <th>
              <i class="fas fa-xs fa-digital-tachograph"></i> {{ $t("ADMIN_LOGS_TABLE_INSTANCE") }}
            </th>
          </tr>
          </thead>

          <tbody>
          <tr v-for="log in logLines">
            <td>{{ log.timestamp }}</td>
            <td>
              <span class="badge" :class="getBadgeClassByLogLevel(log.level)">{{ getLogLevelType(log.level) }}</span>
            </td>
            <td>{{ log.type }}</td>
            <td>{{ log.user }}</td>
            <td>{{ log.message }}</td>
            <td>{{ log.channel }}</td>
            <td>{{ log.instance === null ? '/' : log.instance }}</td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay';
import { Datetime } from 'vue-datetime';
import { DateTime } from 'luxon';

import { RepositoryFactory } from '../../../repositories/RepositoryFactory';

const LogRepository = RepositoryFactory.get('logs');
const ChannelRepository = RepositoryFactory.get('adminChannels');


export default {
  name: 'AdminLogs',
  components: { Loading, Datetime },
  data() {
    return {
      logLines: [],
      filters: {
        user: '',
        search: '',
        channel: null,
        start: null,
        end: null,
        level: null,
      },
      selectedLogLevels: [],
      availableLogLevels: [
        { key: 'INFO', value: 1 },
        { key: 'WARN', value: 2 },
        { key: 'ERROR', value: 4 },
        { key: 'DEBUG', value: 8 },
      ],
      channels: [],
      isLoading: false,
    };
  },
  mounted() {
    this.fetchLogs();
    this.fetchChannels();
  },
  watch: {
    selectedLogLevels(newValue, oldValue) {
      const levels = newValue.flatMap(level => level.value);
      if (levels.length) {
        this.filters.level = levels.reduce((a, b) => a + b);
      } else {
        this.filters.level = null;
      }
    },
  },
  methods: {
    resetFilters() {
      this.filters = {
        user: '',
        search: '',
        channel: null,
        start: null,
        end: null,
        level: null,
      };
      this.selectedLogLevels = [];
      this.fetchLogs();
    },
    async fetchLogs() {
      this.isLoading = true;

      const filters = Object.assign({}, this.filters);
      if (filters.channel !== null) {
        filters.channel = filters.channel.chid;
      }

      if (filters.start !== null && filters.start !== '') filters.start = DateTime.fromISO(filters.start).toFormat('y-LL-dd HH:mm:ss');

      if (filters.end !== null && filters.end !== '') filters.end = DateTime.fromISO(filters.end).toFormat('y-LL-dd HH:mm:ss');

      await LogRepository.get(filters).then((response) => {
        this.logLines = response.data.logs;
      }).finally(() => this.isLoading = false);
    },

    async fetchChannels() {
      this.isLoading = true;
      await ChannelRepository.getAll()
        .then((response) => {
          this.channels = response.data.list;
          this.isLoading = false;
        });
    },

    getBadgeClassByLogLevel(level) {
      switch (level) {
        case 1:
          return 'badge-info';
        case 2:
          return 'badge-warning';
        case 4:
          return 'badge-danger';
        case 8:
          return 'badge-secondary';
      }
    },

    getLogLevelType(level) {
      switch (level) {
        case 1:
          return 'INFO';
        case 2:
          return 'WARN';
        case 4:
          return 'ERROR';
        case 8:
          return 'DEBUG';
      }
    },
  },
};
</script>

<style scoped>
  .table th, .table td {
    padding: 0.25rem;
  }

  table {
    display: flex;
    flex-flow: column;
    width: 100%;
    height: calc(100vh - 450px);
  }

  thead {
    flex: 0 0 auto;
  }

  tbody {
    flex: 1 1 auto;
    display: block;
    overflow-y: auto;
    overflow-x: hidden;
  }

  tr {
    width: 100%;
    display: table;
    table-layout: fixed;
  }

</style>
